/**
 * Desayuno y comida habituales de Janeth, de lunes a viernes
 * (docs/Entrenamiento/yaneth-casa-y-comidas.md, confirmado el 26 oct 2026).
 *
 * - Son plantillas: se proponen en sus días y solo se registran al
 *   confirmarlas, con las cantidades de ese día. No se dan por comidas, no se
 *   llenan días hacia atrás, no se asignan al sábado ni al domingo y no hay
 *   cena fija: la cena se captura cada día. No cambian metas ni el plan 6 + 2.
 * - Se ofrecen en el teléfono que se eligió como de Janeth (Settings.rutinasDe),
 *   no por el nombre del perfil.
 * - Cada alimento propio dice de dónde salen sus valores (Food.calidad) y
 *   guarda en su nota lo que el modelo no tiene: micronutrientes de la
 *   etiqueta, fuente y cómo se calculó. Los de la base (chía, plátano y
 *   aceite de oliva) son referencias de USDA y se usan tal cual.
 * - Precisión completa; se redondea al mostrar. C conserva la definición de
 *   cada fuente (USDA y verduras: totales; Lala: disponibles; Wellthy: la de su
 *   etiqueta) y las grasas saturadas ya van dentro de las totales.
 *
 * Decisiones:
 * - Avena: la del archivo es USDA FDC 169705 (389 kcal por 100 g). La de la
 *   base es otra (FDC 173904, 379 kcal), así que va como alimento propio.
 * - Pollo: la referencia es pechuga sin piel cocida (FDC 171477). El corte no
 *   se confirmó: va como estimación y se puede corregir en Mis alimentos.
 * - Frijoles: modelo provisional de su receta, con el aceite de la receta
 *   incluido; el rendimiento de 2,700 g se supuso, no se midió. La
 *   cucharadita de aceite del pollo va aparte (aceite de oliva de la base).
 * - Lala: por 100 ml y se registra en ml, sin suponer que 1 ml pesa 1 g.
 */

import type { CalidadDato, ComidaHabitual, Food, Nutrientes } from '@/db/types';

/** Un alimento propio que se agrega a Mis alimentos al crear las comidas habituales. */
export type AlimentoDePlantilla = Omit<Food, 'favorito' | 'archivado' | 'creadoEn' | 'actualizadoEn'> & {
  calidad: CalidadDato;
  nota: string;
};

/** Una comida habitual antes de crearla: sin fechas. */
export type ComidaDePlantilla = Omit<ComidaHabitual, 'desde' | 'creadoEn' | 'actualizadoEn'>;

// ---------------------------------------------------------------------------
// Frijoles negros refritos caseros: modelo provisional (autorizado por Janeth)
// ---------------------------------------------------------------------------

/** Frijol negro seco genérico, USDA FDC 173734, por 100 g (el mismo de la base). */
const FRIJOL_SECO_POR_100G: Nutrientes = { kcal: 341, proteinaG: 21.6, carbohidratosG: 62.36, grasaG: 1.42, fibraG: 15.5 };
/** Aceite, referencia aproximada por 100 g; sin marca ni tipo confirmados. */
const ACEITE_POR_100G: Nutrientes = { kcal: 884, proteinaG: 0, carbohidratosG: 0, grasaG: 100, fibraG: 0 };

export const RECETA_FRIJOLES = {
  frijolSecoG: 900,
  /** 20 ml a unos 0.9 g/ml. */
  aceiteG: 20 * 0.9,
  /** SUPUESTO, no medido: cuando se pese el lote, se cambia por ese peso y se recalcula. */
  rendimientoG: 2_700,
} as const;

function porCienDeLaReceta(): Nutrientes {
  const { frijolSecoG, aceiteG, rendimientoG } = RECETA_FRIJOLES;
  const lote = (campo: keyof Nutrientes) =>
    ((FRIJOL_SECO_POR_100G[campo] ?? 0) * frijolSecoG) / 100 + ((ACEITE_POR_100G[campo] ?? 0) * aceiteG) / 100;
  const por100 = (campo: keyof Nutrientes) => (lote(campo) * 100) / rendimientoG;
  return {
    kcal: por100('kcal'),
    proteinaG: por100('proteinaG'),
    carbohidratosG: por100('carbohidratosG'),
    grasaG: por100('grasaG'),
    fibraG: por100('fibraG'),
  };
}

// ---------------------------------------------------------------------------
// Alimentos propios (ids fijos: no se vuelven a crear)
// ---------------------------------------------------------------------------

export const ALIMENTOS_HABITUALES_DE_JANETH: readonly AlimentoDePlantilla[] = [
  {
    id: 'al_hab_wellthy',
    nombre: 'Wellthy',
    referencia: { tipo: 'porPorcion', porcion: { nombre: 'scoop', gramos: 30 } },
    nutrientes: { kcal: 123, proteinaG: 25, carbohidratosG: 0.5, grasaG: 2.4, fibraG: 0.4 },
    porciones: [],
    rompeAyuno: true,
    origenBaseId: null,
    estado: null,
    calidad: 'etiqueta',
    nota:
      'Etiqueta de tu Wellthy, por 1 scoop de 30 g de polvo: 123 kcal (521 kJ), proteína 25 g, grasas totales 2.4 g ' +
      '(saturadas 0.6 g, incluidas en las totales), colesterol 0 mg, carbohidratos 0.5 g, azúcares 0 g, fibra 0.4 g, ' +
      'sodio 51.2 mg, calcio 10 mg, hierro 8 mg, potasio 125 mg; 60 porciones por envase. La columna por 100 g ' +
      '(411 kcal, 83.1 g de proteína) difiere por redondeo: se usa la porción de 30 g.',
  },
  {
    id: 'al_hab_leche_lala_proteina',
    nombre: 'Leche Lala 100 sin lactosa + proteína',
    referencia: { tipo: 'porPorcion', porcion: { nombre: '100 ml', gramos: null } },
    nutrientes: { kcal: 53, proteinaG: 5.4, carbohidratosG: 3.4, grasaG: 2, fibraG: 0 },
    porciones: [],
    rompeAyuno: true,
    origenBaseId: null,
    estado: null,
    calidad: 'etiqueta',
    nota:
      'Etiqueta de Lala 100 sin lactosa pasteurizada + proteína (etiqueta dorada), por 100 ml: 53 kcal, proteína 5.4 g, ' +
      'grasas totales 2.0 g (saturadas 1.2 g, incluidas en las totales; trans 0 g), hidratos de carbono disponibles 3.4 g ' +
      '(azúcares 3.4 g; añadidos 0 g), fibra 0 g, sodio 33 mg, calcio 160 mg, vitamina A 66.4 µg ER, vitamina D 0.5 µg. ' +
      'Se registra en ml, sin suponer que 1 ml pesa 1 g. La columna de 250 ml de la etiqueta no es tu porción.',
  },
  {
    id: 'al_hab_avena_natural',
    nombre: 'Avena natural seca',
    referencia: { tipo: 'por100g' },
    nutrientes: { kcal: 389, proteinaG: 16.89, carbohidratosG: 66.27, grasaG: 6.9, fibraG: 10.6 },
    porciones: [],
    rompeAyuno: true,
    origenBaseId: null,
    estado: 'crudo',
    calidad: 'referencia',
    nota:
      'Referencia genérica de avena seca (USDA FDC 169705), no la etiqueta de tu producto. ' +
      'Es para avena seca o natural: no sirve para avena ya cocida en agua.',
  },
  {
    id: 'al_hab_pollo_plancha',
    nombre: 'Pollo a la plancha',
    referencia: { tipo: 'por100g' },
    nutrientes: { kcal: 165, proteinaG: 31.02, carbohidratosG: 0, grasaG: 3.57, fibraG: 0 },
    porciones: [],
    rompeAyuno: true,
    origenBaseId: 'ab_pechuga_pollo_cocida',
    estado: 'cocido',
    calidad: 'estimada',
    nota:
      'Estimación con pechuga sin piel cocida (USDA FDC 171477), sin contar aceite; no es la etiqueta de tu pollo. ' +
      'El corte no se confirmó: si el tuyo o su preparación es otro, corrige aquí los valores. ' +
      'La cucharadita de aceite de oliva se registra aparte.',
  },
  {
    id: 'al_hab_frijoles_refritos',
    nombre: 'Frijoles negros refritos caseros',
    referencia: { tipo: 'por100g' },
    nutrientes: porCienDeLaReceta(),
    porciones: [],
    rompeAyuno: true,
    origenBaseId: null,
    estado: 'cocido',
    calidad: 'estimada',
    nota:
      'Estimación provisional de tu receta: 900 g de frijol negro seco (USDA FDC 173734) y 20 ml de aceite (unos 18 g) ' +
      'para todo el lote, ya incluidos. Se supuso que el lote rinde 2,700 g de frijoles refritos: no es un peso medido. ' +
      'El agua, el escurrido y el refrito cambian los valores. Sal y condimentos no se cuantificaron: sodio desconocido, ' +
      'no cero. Cuando peses el lote, se recalcula con ese peso.',
  },
  {
    id: 'al_hab_verduras_kirkland',
    nombre: 'Verduras Kirkland Signature estilo Normandía',
    referencia: { tipo: 'porPorcion', porcion: { nombre: 'taza', gramos: 96 } },
    nutrientes: { kcal: 30, proteinaG: 2, carbohidratosG: 5, grasaG: 0, fibraG: 3 },
    porciones: [],
    rompeAyuno: true,
    origenBaseId: null,
    estado: null,
    calidad: 'aproximada',
    nota:
      'Aproximación con la etiqueta de Costco de EE. UU. (Normandy Style Vegetable Blend, 5.5 lb, artículo 1238662): ' +
      'por 1 taza (96 g), 30 kcal, 2 g de proteína, 5 g de carbohidratos totales, 0 g de grasa y 3 g de fibra. ' +
      'No es una etiqueta mexicana ni un análisis ya cocido: la humedad y la presentación pueden cambiarla. ' +
      'Los 0 g de grasa son el redondeo de la etiqueta. Sin aceite, mantequilla ni salsa.',
  },
];

// ---------------------------------------------------------------------------
// Comidas habituales
// ---------------------------------------------------------------------------

export const LUNES_A_VIERNES: readonly number[] = [1, 2, 3, 4, 5];

export const COMIDAS_HABITUALES_DE_JANETH: readonly ComidaDePlantilla[] = [
  {
    id: 'janeth.desayuno',
    nombre: 'Desayuno habitual',
    tiempo: 'desayuno',
    dias: [...LUNES_A_VIERNES],
    items: [
      { origen: 'propio', alimentoId: 'al_hab_wellthy', cantidad: 1, porcion: 'scoop' },
      { origen: 'propio', alimentoId: 'al_hab_leche_lala_proteina', cantidad: 200, porcion: 'ml' },
      { origen: 'propio', alimentoId: 'al_hab_avena_natural', cantidad: 25, porcion: 'g' },
      { origen: 'base', alimentoId: 'ab_chia', cantidad: 10, porcion: 'g' },
      // Aproximadamente 100 g comestibles, sin cáscara
      { origen: 'base', alimentoId: 'ab_platano', cantidad: 100, porcion: 'g' },
    ],
  },
  {
    id: 'janeth.comida',
    nombre: 'Comida habitual',
    tiempo: 'comida',
    dias: [...LUNES_A_VIERNES],
    items: [
      // Pollo, frijoles y verduras se pesan ya cocidos y listos para comer
      { origen: 'propio', alimentoId: 'al_hab_pollo_plancha', cantidad: 200, porcion: 'g' },
      { origen: 'propio', alimentoId: 'al_hab_frijoles_refritos', cantidad: 60, porcion: 'g' },
      { origen: 'propio', alimentoId: 'al_hab_verduras_kirkland', cantidad: 150, porcion: 'g' },
      // La del pollo, aparte del aceite de los frijoles: 1 cucharadita (4.5 g), no una cucharada
      { origen: 'base', alimentoId: 'ab_aceite_oliva', cantidad: 1, porcion: 'cucharadita' },
    ],
  },
];
